/**
 * AssessmentsPanel types
 */

export type ParseStatus = "pending" | "ok" | "error";

/**
 * Send-time copy of a CUSTOM (file-based) instrument, as stored on the
 * assessment row's `instrument_snapshot` (send_questionnaire.py). Items are
 * already localized to a single `lang` at send time (unlike built-in
 * InstrumentDefinition below, which carries all langs) — so item/scale text
 * here is a plain string, not LocalizedText.
 */
export type QuestionnaireSnapshotItem = { id: string; text: string };
export type QuestionnaireSnapshotScaleStep = { value: number; label: string };
export type QuestionnaireSnapshot = {
  name: string;
  timeframe?: string;
  lang: "en" | "hu";
  items: QuestionnaireSnapshotItem[];
  scale: QuestionnaireSnapshotScaleStep[];
};

export type UploadedAssessment = {
  id: string; // assessment_id
  filename: string;
  createdAt: number;

  tpPk?: string;
  sk?: string;

  parserText?: string;

  parseStatus?: ParseStatus;
  parseOutputText?: string;
  parseError?: string;
  parsedAt?: number;
  parseModel?: string;

  kind: "upload" | "questionnaire";

  // questionnaire administration fields
  status?: "sent" | "completed";
  channel?: "app" | "email";
  instrumentId?: string;
  instrumentVersion?: number;
  lang?: "en" | "hu";
  score?: number;
  severity?: string;
  severityTone?: "good" | "caution" | "concern";
  responses?: Record<string, number>;
  sentAt?: number;
  completedAt?: number;

  // custom-questionnaire (file-based) send fields. The list endpoint only
  // ever returns the snapshot NAME (title for the tile); the full snapshot
  // (items/scale, needed to render answers) arrives only from get_assessment
  // when a row is expanded — same convention as `responses` above.
  fileId?: string;
  instrumentSnapshotName?: string;
  instrumentSnapshot?: QuestionnaireSnapshot;

  // true for email-channel sends the therapist marked done manually
  // (patch_questionnaire.py) — these never carry responses/score.
  completedManually?: boolean;
};

export type BusyState = Record<string, "delete" | "parse" | "save" | "markCompleted">;

/**
 * Map raw API item to UploadedAssessment
 */
export function mapItem(it: any): UploadedAssessment {
  const createdAt = typeof it.created_at === "number" ? it.created_at : Date.now();
  const id =
    (it.assessment_id as string | undefined) ??
    (it.id as string | undefined) ??
    `${it.filename ?? "assessment"}-${createdAt}`;

  const ps = it.parse_status ? String(it.parse_status) : undefined;
  const parseStatus: ParseStatus | undefined =
    ps === "pending" || ps === "ok" || ps === "error" ? (ps as ParseStatus) : undefined;

  const tpPk =
    it.tp_pk ?? it.tpPk ?? it.tpPK ?? it.TP_PK ?? it.pk ?? it.PK ?? it.partition_key ?? it.partitionKey;

  const sk = it.sk ?? it.SK ?? it.sort_key ?? it.sortKey ?? it.item_sk ?? it.itemSk;

  // instrument_snapshot: full object only from get_assessment (expanded row);
  // instrument_snapshot_name: name-only from list_assessments (lean list).
  // Prefer the explicit name field; fall back to the full snapshot's name
  // once expanded, so the title stays populated in both places.
  const snapshot: QuestionnaireSnapshot | undefined =
    it.instrument_snapshot && typeof it.instrument_snapshot === "object"
      ? (it.instrument_snapshot as QuestionnaireSnapshot)
      : undefined;
  const instrumentSnapshotName = it.instrument_snapshot_name
    ? String(it.instrument_snapshot_name)
    : snapshot?.name
    ? String(snapshot.name)
    : undefined;

  return {
    id,
    filename: String(it.filename ?? "unknown"),
    createdAt,
    tpPk: tpPk ? String(tpPk) : undefined,
    sk: sk ? String(sk) : undefined,
    parserText: it.parser_text ? String(it.parser_text) : undefined,
    parseStatus,
    parseOutputText: it.parse_output_text ? String(it.parse_output_text) : undefined,
    parseError: it.parse_error ? String(it.parse_error) : undefined,
    parsedAt: typeof it.parsed_at === "number" ? it.parsed_at : undefined,
    parseModel: it.parse_model ? String(it.parse_model) : undefined,
    kind: it.type === "questionnaire" ? "questionnaire" : "upload",
    status: it.status === "sent" || it.status === "completed" ? it.status : undefined,
    channel: it.channel === "app" || it.channel === "email" ? it.channel : undefined,
    instrumentId: it.instrument_id ? String(it.instrument_id) : undefined,
    instrumentVersion: typeof it.instrument_version === "number" ? it.instrument_version : undefined,
    lang: it.lang === "en" || it.lang === "hu" ? it.lang : undefined,
    score: typeof it.score === "number" ? it.score : undefined,
    severity: it.severity ? String(it.severity) : undefined,
    severityTone: it.severity_tone === "good" || it.severity_tone === "caution" || it.severity_tone === "concern" ? it.severity_tone : undefined,
    responses: it.responses && typeof it.responses === "object" ? it.responses : undefined,
    sentAt: typeof it.sent_at === "number" ? it.sent_at : undefined,
    completedAt: typeof it.completed_at === "number" ? it.completed_at : undefined,
    fileId: it.file_id ? String(it.file_id) : undefined,
    instrumentSnapshotName,
    instrumentSnapshot: snapshot,
    completedManually: typeof it.completed_manually === "boolean" ? it.completed_manually : undefined,
  };
}

/**
 * Body for POST /patients/{pid}/questionnaires (Plan 2 Task 4). Exactly one
 * of instrumentId (built-in) / fileId (custom, must be status "ready") is
 * set by the caller — mirrors send_questionnaire.py's validation.
 */
export type SendQuestionnaireParams = {
  instrumentId?: string;
  fileId?: string;
  channel: "app" | "email";
  lang: "en" | "hu";
  attachOriginal?: boolean;
};

export type LocalizedText = { en: string; hu: string };
export type InstrumentBand = {
  min: number; max: number; severity: string;
  tone: "good" | "caution" | "concern"; label: LocalizedText;
};
export type InstrumentDefinition = {
  instrument_id: string;
  version: number;
  name: LocalizedText;
  description: LocalizedText;
  timeframe: LocalizedText;
  items: { id: number; text: LocalizedText }[];
  scale: { value: number; label: LocalizedText }[];
  scoring: { method: "sum"; multiplier: number; bands: InstrumentBand[] };
};
